import type { DragEndEvent } from '@dnd-kit/react';
import type { EditableImageListRecord, ImageShape } from '../../types/index.ts';
import type { ImageListItemProps } from './ImageListItem.tsx';
import { PointerActivationConstraints, PointerSensor } from '@dnd-kit/dom';
import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import clsx from 'clsx';
import { keyBy } from 'es-toolkit/array';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { LuImagePlus } from 'react-icons/lu';
import { PiImages } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { ImageFileSizeRange, ImageMimeExtMap } from '@_/constants';
import { EmptyState, Image } from '@_/ui';
import { i18nMessages } from '../../shared/i18nMessages.ts';
import { ImageListItem } from './ImageListItem.tsx';

export type EditableImageListProps<TImage extends ImageShape = ImageShape> = Partial<
  Pick<ImageListItemProps, 'renderImage' | 'renderPlaceholder'>
> & {
  disabled?: boolean;
  dragAndDrop?: boolean;
  editing?: boolean;
  items: Array<EditableImageListRecord<TImage>>;
  maxImages?: number;
  onAddItem: (file: File) => void;
  onRemoveItem: (itemId: string) => void;
  onMoveItem: (initialIndex: number, index: number) => void;
  renderEmptyState?: (options: { openFileDialog?: () => void }) => React.ReactNode;
  renderUploadButtonContainer?: (props: React.PropsWithChildren) => React.ReactNode;
};

export function EditableImageList<TImage extends ImageShape>({
  disabled = false,
  dragAndDrop = true,
  editing = true,
  items,
  maxImages,
  onAddItem,
  onRemoveItem,
  onMoveItem,
  renderEmptyState = defaultRenderEmptyState,
  renderImage = defaultRenderImage,
  renderPlaceholder = defaultRenderPlaceholder,
  renderUploadButtonContainer = defaultRenderUploadButtonContainer
}: EditableImageListProps<TImage>) {
  const itemMap = useMemo(() => keyBy(items, item => item.id), [items]);

  const [isDropping, setIsDropping] = useState<boolean>(false);
  const setDropping = useCallback(() => setIsDropping(true), []);
  const setNotDropping = useCallback(() => setIsDropping(false), []);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { source } = event.operation;

      if (event.canceled) {
        return;
      }

      if (isSortable(source) && source.initialIndex !== source.index) {
        onMoveItem(source.initialIndex, source.index);
      }
    },
    [onMoveItem]
  );

  const handleDrop = useCallback(
    (files: File[]) => {
      files.forEach(onAddItem);
      setNotDropping();
    },
    [setNotDropping, onAddItem]
  );

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog
  } = useDropzone({
    accept: ImageMimeExtMap,
    minSize: ImageFileSizeRange.min,
    maxSize: ImageFileSizeRange.max,
    multiple: true,
    noClick: true,
    onDragEnter: setDropping,
    onDragLeave: setNotDropping,
    onDrop: handleDrop
  });

  const gridStyles = clsx(
    'grid grid-cols-3 gap-4 @md:grid-cols-4 @xl:grid-cols-5 @3xl:grid-cols-6 @4xl:grid-cols-7 @5xl:grid-cols-8 @6xl:grid-cols-9'
  );

  const reachedMaxImages = typeof maxImages === 'number' && items?.length >= maxImages;
  const canUpload = !disabled && !reachedMaxImages;
  const canDropAndDrop = canUpload && dragAndDrop;

  if (disabled || !editing) {
    return (
      <div className="@container relative select-none" {...(canDropAndDrop && getRootProps())}>
        {canDropAndDrop && <input {...getInputProps()} />}
        {isDropping && <FileDropIndicator />}

        <div className={clsx(isDropping && 'opacity-0')}>
          {items.length ? (
            <div className={gridStyles}>
              {items.map((item, index) =>
                renderImage({ key: item.id, imageUrl: item.imageUrl, index })
              )}
            </div>
          ) : (
            renderEmptyState?.({
              openFileDialog: canUpload ? openFileDialog : undefined
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="@container relative flex h-full grow flex-col items-stretch select-none"
      {...(canDropAndDrop && getRootProps())}
    >
      {canDropAndDrop && <input {...getInputProps()} />}
      {isDropping && <FileDropIndicator />}

      <div className={clsx(isDropping && 'opacity-10')}>
        {!!items.length && (
          <DragDropProvider
            onDragEnd={handleDragEnd}
            sensors={defaults => [
              ...defaults.filter(sensor => sensor !== PointerSensor),
              PointerSensor.configure({
                activationConstraints(event) {
                  if (event.pointerType === 'touch') {
                    return [
                      new PointerActivationConstraints.Delay({
                        value: 500,
                        tolerance: { x: 5, y: 5 }
                      })
                    ];
                  }
                  return [new PointerActivationConstraints.Distance({ value: 8 })];
                }
              })
            ]}
          >
            <div ref={containerRef} className={gridStyles}>
              {items.map((item, index) => (
                <ImageListItem
                  key={item.id}
                  containerRef={containerRef}
                  index={index}
                  item={item}
                  onRemove={onRemoveItem}
                  renderImage={renderImage}
                  renderPlaceholder={renderPlaceholder}
                />
              ))}

              {canUpload &&
                renderUploadButtonContainer({
                  children: (
                    <button
                      className="flex size-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white text-gray-400 hover:border-gray-400 hover:text-gray-600"
                      onClick={openFileDialog}
                      type="button"
                    >
                      <LuImagePlus className="size-8" />
                    </button>
                  )
                })}
            </div>

            {createPortal(
              <DragOverlay>
                {source => (
                  <div className="shadow-2xl">
                    {renderImage({
                      imageUrl: itemMap[source.id].imageUrl,
                      index: -1
                    })}
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </DragDropProvider>
        )}
      </div>

      {!items.length &&
        !isDropping &&
        renderEmptyState({
          openFileDialog: canUpload ? openFileDialog : undefined
        })}
    </div>
  );
}

function FileDropIndicator() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center rounded-lg border-4 border-dashed border-primary-300 text-xl font-semibold text-primary-500">
      <FormattedMessage {...i18nMessages.dropUploadFiles} />
    </div>
  );
}

function defaultRenderImage({ key, imageUrl }: { key?: string; imageUrl: string }) {
  return <Image key={key} className="aspect-square rounded-lg" fit="cover" src={imageUrl} />;
}

function defaultRenderPlaceholder() {
  return (
    <div className="aspect-square w-full rounded-lg border-2 border-dashed border-primary-400 bg-primary-100" />
  );
}

function defaultRenderUploadButtonContainer(props: React.PropsWithChildren) {
  return <div {...props} className="aspect-square" />;
}

function defaultRenderEmptyState({ openFileDialog }: { openFileDialog?: () => void }) {
  return (
    <button className="block size-full grow bg-gray-100" onClick={openFileDialog} type="button">
      <EmptyState
        heading={
          <FormattedMessage
            id="component:prompt.click-to-add-photos"
            defaultMessage="Click to add photos"
          />
        }
        icon={<PiImages />}
        message={<FormattedMessage {...i18nMessages.orDragAndDrop} />}
      />
    </button>
  );
}
